import type { MediaState, SavedMediaState, MediaStateSaveReason } from '../types/media-state';
import { MEDIA_CONFIG, DEFAULT_MEDIA_STATE } from '../const/media-config';

/**
 * 미디어 상태 저장/복원 유틸리티
 *
 * 책임:
 * - 미디어 상태 저장
 * - 만료 시간 관리
 * - 상태 복원 로직
 */

// 메모리 내 저장소 (세션 유지)
let savedMediaState: SavedMediaState | null = null;

/**
 * 현재 미디어 상태 저장
 */
export const saveMediaState = (
  state: MediaState,
  reason: MediaStateSaveReason = 'manual'
): void => {
  savedMediaState = {
    state: { ...state },
    savedAt: Date.now(),
    reason,
  };

  if (MEDIA_CONFIG.ENABLE_LOGGING) {
    console.log('💾 [MEDIA] 상태 저장:', {
      ...savedMediaState,
      expiresIn: `${MEDIA_CONFIG.STATE_EXPIRATION_TIME / 1000}초`,
    });
  }
};

/**
 * 저장된 미디어 상태 가져오기
 * 만료된 경우 null 반환
 */
export const getSavedMediaState = (): SavedMediaState | null => {
  if (!savedMediaState) {
    return null;
  }

  // 만료 확인
  const now = Date.now();
  const elapsed = now - savedMediaState.savedAt;

  if (elapsed > MEDIA_CONFIG.STATE_EXPIRATION_TIME) {
    if (MEDIA_CONFIG.ENABLE_LOGGING) {
      console.log('⏰ [MEDIA] 저장된 상태 만료됨 - 삭제');
    }
    clearSavedMediaState();
    return null;
  }

  return savedMediaState;
};

/**
 * 저장된 상태 삭제
 */
export const clearSavedMediaState = (): void => {
  savedMediaState = null;

  if (MEDIA_CONFIG.ENABLE_LOGGING) {
    console.log('🗑️ [MEDIA] 저장된 상태 삭제됨');
  }
};

/**
 * 저장된 상태 존재 여부 확인
 */
export const hasSavedMediaState = (): boolean => {
  return getSavedMediaState() !== null;
};

/**
 * 저장된 상태로 복원
 * @returns 복원된 상태 (없으면 기본값)
 */
export const restoreMediaState = (): MediaState => {
  const saved = getSavedMediaState();

  if (!saved) {
    if (MEDIA_CONFIG.ENABLE_LOGGING) {
      console.log('ℹ️ [MEDIA] 저장된 상태 없음 - 기본값 사용');
    }
    return { ...DEFAULT_MEDIA_STATE };
  }

  if (MEDIA_CONFIG.ENABLE_LOGGING) {
    console.log('🔄 [MEDIA] 상태 복원:', saved.state);
  }

  // 복원 후 삭제 (일회성)
  clearSavedMediaState();

  return { ...saved.state };
};

/**
 * 상태 병합 (부분 복원용)
 */
export const mergeMediaState = (
  current: MediaState,
  saved: Partial<MediaState>
): MediaState => {
  return {
    ...current,
    ...saved,
  };
};
